import React, { Suspense, useState, useEffect, lazy } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';

// Providers
import authProvider, { authEvents, AUTH_EVENTS } from './providers/authProvider';
import dataProvider from './providers/dataProvider';
import i18nProvider from './providers/i18nProvider';

// Layout con HotelProvider integrado
import { CustomLayout } from './layouts';

// Error Boundary
import ErrorBoundary from './components/common/ErrorBoundary';

// Componentes (auth pages loaded eagerly — needed before app renders)
import CustomLoginPage from './components/auth/CustomLoginPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

// Lazy-loaded route components
const Dashboard = lazy(() => import('./features/dashboard/components/Dashboard'));
const NotAuthorized = lazy(() => import('./components/common/NotAuthorized'));
const HotelConfigPage = lazy(() => import('./features/hotels/components/config/HotelConfigPage').then(m => ({ default: m.HotelConfigPage })));
const HotelZones = lazy(() => import('./features/hotels').then(m => ({ default: m.HotelZones })));
const HotelTexts = lazy(() => import('./features/hotels').then(m => ({ default: m.HotelTexts })));
const HotelNotifications = lazy(() => import('./features/hotels').then(m => ({ default: m.HotelNotifications })));
const HotelFields = lazy(() => import('./features/hotels').then(m => ({ default: m.HotelFields })));
const HotelPmsConfiguration = lazy(() => import('./features/pms').then(m => ({ default: m.HotelPmsConfiguration })));
const GuestAccessConfiguration = lazy(() => import('./features/guest-access').then(m => ({ default: m.GuestAccessConfiguration })));

// Tema - v3 professional
import lightTheme from './lightTheme';

// Configuración de recursos
import { getResourcesForReactAdmin } from './config/resourceConfig';

// Query client con caché
import queryClient from './config/queryClient';

const App: React.FC = () => {
  const [resources, setResources] = useState(() => getResourcesForReactAdmin());
  const [adminKey, setAdminKey] = useState(0);
  
  useEffect(() => {
    const reloadResources = () => {
      const newResources = getResourcesForReactAdmin();
      setResources(newResources);
      setAdminKey(prev => prev + 1);
    };
    
    const handleLoginSuccess = (event: Event) => {
      setTimeout(() => {
        reloadResources();
      }, 100);
    };
    
    const handleLogout = () => {
      reloadResources();
    };
    
    const handleRoleChanged = () => {
      reloadResources();
    };
    
    // Agregar listeners
    authEvents.addEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
    authEvents.addEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
    authEvents.addEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);
    
    // Cleanup
    return () => {
      authEvents.removeEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
      authEvents.removeEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
      authEvents.removeEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);
    };
  }, []);
  
  return (
    <Suspense 
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      }
    >
      <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <Admin
          key={adminKey}
          queryClient={queryClient}
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          theme={lightTheme}
          layout={CustomLayout}
          loginPage={CustomLoginPage}
          dashboard={Dashboard}

          requireAuth
          disableTelemetry
        >
          {/* Recursos dinámicos desde resourceConfig */}
          {resources.map(resource => (
            <Resource 
              key={resource.name}
              {...resource}
            />
          ))}

          {/* Rutas personalizadas */}
          <CustomRoutes>
            <Route path="/not-authorized" element={<NotAuthorized />} />

            {/* Ruta unificada de configuración de Hotel */}
            <Route path="/hotels/:id/config" element={<HotelConfigPage />} />

            {/* Rutas legacy de configuración de Hotels (se mantienen por compatibilidad) */}
            <Route path="/hotels/:id/zones" element={<HotelZones />} />
            <Route path="/hotels/:id/texts" element={<HotelTexts />} />
            <Route path="/hotels/:id/notifications" element={<HotelNotifications />} />
            <Route path="/hotels/:id/fields" element={<HotelFields />} />
            <Route path="/hotels/:id/pms" element={<HotelPmsConfiguration />} />
            <Route path="/hotels/:id/guest-access" element={<GuestAccessConfiguration />} />
          </CustomRoutes>

          {/* Rutas de autenticación sin protección */}
          <CustomRoutes noLayout>
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </CustomRoutes>
        </Admin>
      </StyledEngineProvider>
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;